import { Check, Copy } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button/index'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import useTranslator from '@/hooks/translation'
import { cn } from '@/lib/utils'
import { copyToClipboard } from '@/utils/copy-to-clipboard'

import { CopyButtonProps } from './types'

export function CopyButton({
  value,
  copiedText,
  copyText,
  size = 'icon',
  variant = 'ghost',
  showTooltip = true,
  resetDelay = 2000,
  onCopySuccess,
  onCopyError,
  className,
  ...buttonProps
}: CopyButtonProps) {
  const { getTranslation } = useTranslator()

  const [isCopied, setIsCopied] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const defaultCopyText = getTranslation('components.copyButton.copy')
  const defaultCopiedText = getTranslation('components.copyButton.copied')

  const tooltipText = isCopied
    ? (copiedText ?? defaultCopiedText)
    : (copyText ?? defaultCopyText)

  const handleCopy = async () => {
    const success = await copyToClipboard(value)

    if (success) {
      setIsCopied(true)
      onCopySuccess?.(value)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setIsCopied(false)
      }, resetDelay)
    } else {
      onCopyError?.(value)
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const button = (
    <Button
      variant={variant}
      size={size}
      onClick={handleCopy}
      className={cn(
        'h-6 w-6 rounded-full bg-background p-0.5 shadow-sm',
        className,
      )}
      title={tooltipText}
      aria-label={tooltipText}
      {...buttonProps}
    >
      {isCopied ? (
        <Check className="h-3 w-3 animate-bounce" />
      ) : (
        <Copy className="h-3 w-3" />
      )}
    </Button>
  )

  if (!showTooltip) {
    return button
  }

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side="bottom" className="text-xs">
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  )
}
